import { onBeforeUnmount, onMounted, ref, watch, type Ref } from 'vue';

import { BreakpointsWithDefault, getBreakpoint } from '../utils';

export function useBreakpointMediaQueries(breakpoints: BreakpointsWithDefault[]): Ref<BreakpointsWithDefault> {
  const breakpointsMap = ref(new Map<MediaQueryList, BreakpointsWithDefault>());
  const currentBreakpoint = ref<BreakpointsWithDefault>('');

  function handleMediaMatch(mq: MediaQueryList): void {
    let currentMq: MediaQueryList = mq;

    if (mq.matches) {
      currentBreakpoint.value = breakpointsMap.value.get(currentMq) ?? '';
      return;
    }

    const keys: MediaQueryList[] = Array.from(breakpointsMap.value.keys());
    let index: number = keys.indexOf(currentMq);

    while (!currentMq.matches && index > 0) {
      currentMq = keys[--index];
    }

    currentBreakpoint.value = currentMq.matches ? (breakpointsMap.value.get(currentMq) ?? '') : '';
  }

  function onMediaQueryChange(event: MediaQueryListEvent): void {
    handleMediaMatch(event.target as MediaQueryList);
  }

  onMounted(() => {
    const queries: [MediaQueryList, BreakpointsWithDefault][] = [];

    for (const breakpoint of breakpoints) {
      const breakpointValue = getBreakpoint(breakpoint);
      const mq: MediaQueryList = window.matchMedia(`screen and (min-width: ${breakpointValue}px)`);
      mq.addEventListener('change', onMediaQueryChange);

      queries.push([mq, breakpoint]);
    }

    breakpointsMap.value = new Map(queries);
  });

  onBeforeUnmount(() => {
    for (const mq of breakpointsMap.value.keys()) {
      mq?.removeEventListener('change', onMediaQueryChange);
    }
  });

  watch(breakpointsMap, (newValue) => {
    for (const mq of newValue.keys()) {
      handleMediaMatch(mq);
    }
  });

  return currentBreakpoint;
}
